const asText = value => String(value || '').trim()

const asId = value => {
  if (value == null || value === '') return ''
  if (typeof value === 'object') {
    return String(value._id || value.id || '').trim()
  }
  return String(value).trim()
}

const timeOf = value => {
  if (!value) return null
  const time = new Date(value).getTime()
  return Number.isFinite(time) ? time : null
}

export const HISTORY_COUNT_TYPE_FILTERS = Object.freeze([
  'ALL',
  'MAIN',
  'PARTIAL',
])

export const normalizeHistoryCountType = stockTake => {
  const countType = String(stockTake?.countType || '').toUpperCase()
  return countType === 'PARTIAL' ? 'PARTIAL' : 'MAIN'
}

export const normalizeHistoryCountTypeFilter = value => {
  const next = String(value || '').toUpperCase()
  if (next === 'MAIN' || next === 'PARTIAL') return next
  return 'ALL'
}

export const stockTakeHistoryCountTypeFilterStorageKey = venueId =>
  `oms:stockTakeHistoryCountType:${venueId}`

const defaultHistoryFilterStorage = () => {
  if (typeof sessionStorage === 'undefined') return null
  return sessionStorage
}

export const readStockTakeHistoryCountTypeFilter = (
  venueId,
  storage = defaultHistoryFilterStorage(),
) => {
  if (!venueId || !storage) return 'ALL'
  try {
    return normalizeHistoryCountTypeFilter(
      storage.getItem(stockTakeHistoryCountTypeFilterStorageKey(venueId)),
    )
  } catch {
    return 'ALL'
  }
}

export const writeStockTakeHistoryCountTypeFilter = (
  venueId,
  filter,
  storage = defaultHistoryFilterStorage(),
) => {
  const next = normalizeHistoryCountTypeFilter(filter)
  if (!venueId || !storage) return next
  try {
    storage.setItem(stockTakeHistoryCountTypeFilterStorageKey(venueId), next)
  } catch {
    // Private mode / quota — still return the session value.
  }
  return next
}

/** Take detail (not the list). Fatal errors here should not leave Main stuck. */
export const isStockTakeDetailPath = pathname =>
  /\/stockTakes\/[^/?#]+/.test(String(pathname || ''))

export const resetStockTakeHistoryCountTypeFilterOnTakeError = ({
  venueId,
  pathname,
  storage = defaultHistoryFilterStorage(),
} = {}) => {
  if (!isStockTakeDetailPath(pathname)) {
    return readStockTakeHistoryCountTypeFilter(venueId, storage)
  }
  return writeStockTakeHistoryCountTypeFilter(venueId, 'ALL', storage)
}

/**
 * After create/repeat, keep a chip that still shows the new take.
 * Main would hide a supplier / partial count; bump to All instead of forcing
 * Partial so the chef still sees both.
 */
export const countTypeFilterShowingCreatedTake = ({
  currentFilter,
  created,
  supplierIds,
} = {}) => {
  const current = normalizeHistoryCountTypeFilter(currentFilter)
  const createdType = created
    ? normalizeHistoryCountType(created)
    : Array.isArray(supplierIds) && supplierIds.length
    ? 'PARTIAL'
    : 'MAIN'
  if (current === 'ALL' || current === createdType) return current
  return 'ALL'
}

export const revealCreatedTakeInHistory = ({
  venueId,
  currentFilter,
  created,
  supplierIds,
  storage = defaultHistoryFilterStorage(),
} = {}) =>
  writeStockTakeHistoryCountTypeFilter(
    venueId,
    countTypeFilterShowingCreatedTake({
      currentFilter,
      created,
      supplierIds,
    }),
    storage,
  )

export const STOCK_TAKE_HISTORY_SEARCH_PLACEHOLDER = 'Search by name or group'

export const stockTakeHistorySearchHaystack = stockTake => {
  const name = asText(stockTake?.name)
  const heading = asText(stockTake?.listHeading)
  const group =
    asText(stockTake?.partialGroupName) || asText(stockTake?.partialGroup?.name)
  const titles = Array.isArray(stockTake?.supplierTitles)
    ? stockTake.supplierTitles.map(asText).filter(Boolean).join(' ')
    : asText(stockTake?.supplierTitle)
  return `${name} ${heading} ${group} ${titles}`.toLowerCase()
}

export const matchesStockTakeHistorySearch = (stockTake, query) => {
  const needle = asText(query).toLowerCase()
  if (!needle) return true
  return stockTakeHistorySearchHaystack(stockTake).includes(needle)
}

export const STOCK_TAKE_HISTORY_SUPPLIER_PLACEHOLDER = 'Filter by supplier'

export const normalizeHistorySupplierIds = supplierIds => {
  const list = Array.isArray(supplierIds)
    ? supplierIds
    : supplierIds
    ? [supplierIds]
    : []
  return [
    ...new Set(list.map(asId).filter(id => id && id.toUpperCase() !== 'ALL')),
  ]
}

export const toggleHistorySupplierId = (current, supplierId) => {
  const id = asId(supplierId)
  const next = normalizeHistorySupplierIds(current)
  if (!id) return next
  return next.includes(id) ? next.filter(item => item !== id) : [...next, id]
}

/** Take matches if it stored any of the selected supplier ids. */
export const matchesStockTakeHistorySupplier = (stockTake, supplierIds) => {
  const wanted = normalizeHistorySupplierIds(supplierIds)
  if (!wanted.length) return true
  const ids = Array.isArray(stockTake?.supplierIds)
    ? stockTake.supplierIds.map(asId).filter(Boolean)
    : []
  return wanted.some(id => ids.includes(id))
}

export const STOCK_TAKE_HISTORY_DATE_PLACEHOLDER = 'Filter by dates'

export const matchesStockTakeHistoryDateRange = (stockTake, dateRange) => {
  const start = timeOf(dateRange?.start)
  const end = timeOf(dateRange?.end)
  if (start == null || end == null) return true
  const from = timeOf(stockTake?.fromPeriod)
  const to = timeOf(stockTake?.toPeriod) ?? from
  if (from == null) return false
  const takeEnd = to == null ? from : to
  return from <= end && takeEnd >= start
}

export const matchesStockTakeHistoryLocked = (stockTake, lockedFilter) => {
  const locked = String(lockedFilter || 'ALL').toUpperCase()
  if (locked === 'ALL' || locked === '') return true
  if (locked === 'LOCKED') return Boolean(stockTake?.isLocked)
  if (locked === 'UNLOCKED') return !stockTake?.isLocked
  return true
}

export const listStockTakeHistorySupplierOptions = ({
  stockTakes = [],
  venueOptions = [],
} = {}) => {
  const labels = new Map()
  ;(Array.isArray(venueOptions) ? venueOptions : []).forEach(option => {
    const id = asId(option?.value)
    if (!id) return
    labels.set(id, asText(option?.label) || 'Supplier')
  })
  const seen = new Set()
  ;(Array.isArray(stockTakes) ? stockTakes : []).forEach(stockTake => {
    const ids = Array.isArray(stockTake?.supplierIds)
      ? stockTake.supplierIds.map(asId).filter(Boolean)
      : []
    const titles = Array.isArray(stockTake?.supplierTitles)
      ? stockTake.supplierTitles.map(asText)
      : []
    ids.forEach((id, index) => {
      seen.add(id)
      if (labels.has(id)) return
      labels.set(id, titles[index] || 'Supplier')
    })
  })
  return [...seen]
    .map(value => ({
      value,
      label: labels.get(value) || 'Supplier',
    }))
    .sort((left, right) => left.label.localeCompare(right.label))
}

/**
 * History list filters. Date and locked stay in the pipeline; search and
 * supplier sit alongside them.
 */
export const filterStockTakeHistory = (
  stocks = [],
  {
    countTypeFilter = 'ALL',
    partialGroupFilter = 'ALL',
    partialGroupLabel,
    search = '',
    supplierId = '',
    supplierIds = [],
    dateRange,
    lockedFilter = 'ALL',
  } = {},
) =>
  (Array.isArray(stocks) ? stocks : []).filter(stockTake => {
    const countType = normalizeHistoryCountType(stockTake)
    if (countTypeFilter !== 'ALL' && countType !== countTypeFilter) {
      return false
    }
    if (partialGroupFilter !== 'ALL') {
      if (countType !== 'PARTIAL') return false
      const label =
        typeof partialGroupLabel === 'function'
          ? partialGroupLabel(stockTake)
          : stockTake?.partialGroupName
      if (label !== partialGroupFilter) return false
    }
    if (!matchesStockTakeHistorySearch(stockTake, search)) return false
    if (
      !matchesStockTakeHistorySupplier(
        stockTake,
        normalizeHistorySupplierIds(supplierIds).length
          ? supplierIds
          : supplierId,
      )
    ) {
      return false
    }
    if (!matchesStockTakeHistoryDateRange(stockTake, dateRange)) return false
    if (!matchesStockTakeHistoryLocked(stockTake, lockedFilter)) return false
    return true
  })

export const countHiddenSupplierTakes = (stocks = [], countTypeFilter) => {
  if (normalizeHistoryCountTypeFilter(countTypeFilter) !== 'MAIN') return 0
  return (Array.isArray(stocks) ? stocks : []).filter(
    stockTake => normalizeHistoryCountType(stockTake) === 'PARTIAL',
  ).length
}

export const stockTakeHiddenSupplierCountsLine = count => {
  const n = Number(count) || 0
  if (n <= 0) return ''
  if (n === 1) return '1 supplier count hidden.'
  return `${n} supplier counts hidden.`
}

/** Combobox is Partial-only. The helper stays on All and Partial. */
export const shouldShowPartialGroupCombobox = countTypeFilter =>
  normalizeHistoryCountTypeFilter(countTypeFilter) === 'PARTIAL'

export const shouldShowPartialGroupFilterHelper = countTypeFilter => {
  const filter = normalizeHistoryCountTypeFilter(countTypeFilter)
  return filter === 'ALL' || filter === 'PARTIAL'
}
